import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PageHeader from '@/components/common/PageHeader';
import PrivacyConsentNotice from '@/components/common/PrivacyConsentNotice';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setIsSubscribed(true);
      toast.success('Successfully subscribed to our newsletter!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('You are already subscribed!');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="newsletter-page">
      <PageHeader 
        title="Subscribe to Our Newsletter"
        subtitle="Stay updated with the latest immigration news, visa updates, and job opportunities"
        breadcrumbs={[{ label: 'Newsletter' }]}
      />

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <motion.div
              className="bg-white rounded-2xl p-8 md:p-12 shadow-xl text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {isSubscribed ? (
                <div className="py-8">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-zafco-blue mb-4">Thank You for Subscribing!</h2>
                  <p className="text-gray-600">
                    You'll receive our latest updates, immigration news, and exclusive offers directly in your inbox.
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-full bg-zafco-blue/10 flex items-center justify-center mx-auto mb-6">
                    <Mail className="w-10 h-10 text-zafco-blue" />
                  </div>
                  <h2 className="text-2xl font-bold text-zafco-blue mb-4">
                    Get Overseas Job & Immigration Updates
                  </h2>
                  <p className="text-gray-600 mb-8">
                    Subscribe to receive weekly updates on Express Entry draws, visa policy changes, 
                    job opportunities, and expert tips for your immigration journey.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 h-14 text-lg"
                        data-testid="newsletter-email"
                      />
                    </div>
                    <PrivacyConsentNotice tone="light" testid="newsletter-consent" className="text-left" />
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full h-14 bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold text-lg"
                      data-testid="newsletter-submit"
                    >
                      {isSubmitting ? 'Subscribing...' : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Subscribe Now
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-sm text-gray-500 mt-6">
                    We respect your privacy. Unsubscribe at any time.
                  </p>
                </>
              )}
            </motion.div>

            {/* Benefits */}
            <motion.div
              className="mt-12 grid md:grid-cols-3 gap-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {[
                { title: 'Weekly Updates', description: 'Latest Express Entry draws and visa news' },
                { title: 'Job Alerts', description: 'Exclusive overseas job opportunities' },
                { title: 'Expert Tips', description: 'Immigration advice from our experts' },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 text-center">
                  <h3 className="font-bold text-zafco-blue mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsletterPage;
